import { API_BASE_URL, apiHeaders, fetchWithTimeout } from "@/config/apiBase";

export async function fetchJiraIssuesByKeys(keys) {
  const list = (Array.isArray(keys) ? keys : []).map((k) => String(k || "").trim()).filter(Boolean);
  if (list.length === 0) return [];
  const res = await fetchWithTimeout(
    `${API_BASE_URL}/api/jira/issues-by-keys?keys=${encodeURIComponent(list.join(","))}`,
    { headers: apiHeaders() }
  );
  const data = await res.json();
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Failed to load Jira issues");
  }
  return Array.isArray(data) ? data : [];
}

// GET /api/jira/my-issues
// Returns assigned Jira issues with dueDate + nearest sprint + linked Plan Management tasks.
export async function fetchJiraMyIssues(userId) {
  const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/my-issues${qs}`, {
    headers: apiHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Failed to load Jira issues");
  }
  if (Array.isArray(data)) {
    return { issues: data, meta: null };
  }
  return {
    issues: Array.isArray(data.issues) ? data.issues : [],
    meta: data.meta && typeof data.meta === "object" ? data.meta : null,
  };
}

// GET /api/jira/my-issues-categories
// Returns { assigned, reporter, done, meta }
export async function fetchJiraMyIssuesCategories(userId) {
  const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/my-issues-categories${qs}`, {
    headers: apiHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Failed to load Jira issues");
  }
  return {
    assigned: Array.isArray(data.assigned) ? data.assigned : [],
    reporter: Array.isArray(data.reporter) ? data.reporter : [],
    done: Array.isArray(data.done) ? data.done : [],
    meta: data.meta && typeof data.meta === "object" ? data.meta : null,
  };
}

export async function fetchJiraIssueDetail(issueKey) {
  const key = String(issueKey || "").trim();
  if (!key) throw new Error("issue key required");
  const res = await fetchWithTimeout(
    `${API_BASE_URL}/api/jira/issue/${encodeURIComponent(key)}`,
    { headers: apiHeaders() }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Failed to load Jira issue");
  }
  return data;
}

export async function fetchJiraTransitions(issueKey) {
  const key = String(issueKey || "").trim();
  if (!key) return { status: "", transitions: [] };
  const res = await fetchWithTimeout(
    `${API_BASE_URL}/api/jira/transitions/${encodeURIComponent(key)}`,
    { headers: apiHeaders() }
  );
  const data = await res.json();
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Failed to load transitions");
  }
  return {
    status: data.status || "",
    transitions: Array.isArray(data.transitions) ? data.transitions : [],
  };
}

export async function updateJiraDueDate(issueKey, dueDate) {
  const key = String(issueKey || "").trim();
  if (!key) throw new Error("issue key required");
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issue/${encodeURIComponent(key)}`, {
    method: "PATCH",
    headers: apiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ dueDate: dueDate || null }),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error || json));
  }
  return json;
}

export async function fetchJiraAssignableUsers({ projectKey, issueKey, query } = {}) {
  const params = new URLSearchParams();
  if (issueKey) params.set("issueKey", String(issueKey).trim());
  if (projectKey) params.set("projectKey", String(projectKey).trim());
  if (query) params.set("query", String(query).trim());
  const res = await fetchWithTimeout(
    `${API_BASE_URL}/api/jira/assignable-users?${params.toString()}`,
    { headers: apiHeaders() }
  );
  const data = await res.json().catch(() => []);
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Failed to load assignable users");
  }
  return Array.isArray(data) ? data : [];
}

export async function assignJiraIssue(issueKey, accountId) {
  const key = String(issueKey || "").trim();
  if (!key) throw new Error("issue key required");
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issue/${encodeURIComponent(key)}/assignee`, {
    method: "PUT",
    headers: apiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ accountId: accountId || null }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error || json));
  }
  return json;
}

export async function commentJiraIssue(issueKey, body) {
  const key = String(issueKey || "").trim();
  const text = String(body || "").trim();
  if (!key) throw new Error("issue key required");
  if (!text) throw new Error("comment is required");
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issue/${encodeURIComponent(key)}/comment`, {
    method: "POST",
    headers: apiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ body: text }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error || json));
  }
  return json;
}

export async function applyJiraTransition(issueKey, transitionId) {
  const key = String(issueKey || "").trim();
  if (!key || !transitionId) throw new Error("issue key and transition required");
  const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/transition`, {
    method: "POST",
    headers: apiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ key, transitionId }),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error || json));
  }
  return fetchJiraTransitions(key);
}

/** Merge issues-by-keys fields into link rows ({ key, url, ... }). */
export function enrichJiraLinkRows(links, issuesByKey) {
  const byKey = new Map(
    (Array.isArray(issuesByKey) ? issuesByKey : []).map((d) => [
      String(d.key || "").toUpperCase(),
      d,
    ])
  );
  return (Array.isArray(links) ? links : []).map((j) => {
    const info = byKey.get(String(j.key || "").toUpperCase());
    if (!info) return { ...j, transitions: j.transitions || [] };
    return {
      ...j,
      summary: info.summary ?? j.summary ?? "",
      status: info.status ?? j.status ?? "",
      url: info.url || j.url,
      transitions: Array.isArray(info.transitions) ? info.transitions : j.transitions || [],
    };
  });
}
